import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  TrendingUp,
  TrendingDown,
  ArrowRight,
  Clock,
  Server,
  Zap,
  Globe,
  Package,
  Users,
  FileText,
  Download,
  Eye,
  Loader2
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { useAdminAuth } from '@/services/adminAuth';
import {
  getDashboardStats,
  getRecentActivity,
  FirestoreActivityLog
} from '@/services/firebase/adminFirestore';

interface StatCard {
  label: string;
  value: string | number;
  change?: { value: number; trend: 'up' | 'down' };
  icon: React.ReactNode;
  color: string;
  href?: string;
}

interface QuickAction {
  label: string;
  description: string;
  href: string;
  icon: React.ReactNode;
  color: string;
}

// Helper function to format relative time
function formatRelativeTime(date: Date): string {
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) {
    return 'Just now';
  }

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return `${diffInMinutes} minute${diffInMinutes === 1 ? '' : 's'} ago`;
  }

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return `${diffInHours} hour${diffInHours === 1 ? '' : 's'} ago`;
  }

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) {
    return `${diffInDays} day${diffInDays === 1 ? '' : 's'} ago`;
  }

  return date.toLocaleDateString();
}

export function AdminDashboard() {
  const { session } = useAdminAuth();
  const [stats, setStats] = useState<StatCard[]>([]);
  const [recentActivity, setRecentActivity] = useState<FirestoreActivityLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function loadDashboardData() {
      try {
        setLoading(true);
        setError(null);

        // Fetch dashboard stats and recent activity from Firebase
        const [dashboardStats, activityLogs] = await Promise.all([
          getDashboardStats(),
          getRecentActivity(10)
        ]);

        // Build stats cards from Firebase data
        setStats([
          {
            label: 'Total Users',
            value: dashboardStats.totalUsers,
            change: dashboardStats.activeUsers > 0
              ? { value: Math.round((dashboardStats.activeUsers / dashboardStats.totalUsers) * 100), trend: 'up' }
              : undefined,
            icon: <Users size={24} />,
            color: 'text-blue-500',
            href: '/admin/users'
          },
          {
            label: 'Extensions',
            value: dashboardStats.totalExtensions,
            change: dashboardStats.pendingExtensions > 0
              ? { value: dashboardStats.pendingExtensions, trend: 'up' }
              : undefined,
            icon: <Package size={24} />,
            color: 'text-purple-500',
            href: '/admin/extensions'
          },
          {
            label: 'Content',
            value: dashboardStats.totalContent,
            change: dashboardStats.publishedContent > 0
              ? { value: Math.round((dashboardStats.publishedContent / dashboardStats.totalContent) * 100) || 0, trend: 'up' }
              : undefined,
            icon: <FileText size={24} />,
            color: 'text-green-500',
            href: '/admin/content'
          },
          {
            label: 'Total Downloads',
            value: dashboardStats.totalDownloads.toLocaleString(),
            icon: <Download size={24} />,
            color: 'text-accent',
            href: '/admin/analytics'
          },
          {
            label: 'Active Users',
            value: dashboardStats.activeUsers,
            icon: <Activity size={24} />,
            color: 'text-emerald-500',
            href: '/admin/users'
          },
          {
            label: 'Today Page Views',
            value: dashboardStats.todayPageViews.toLocaleString(),
            icon: <Eye size={24} />,
            color: 'text-orange-500',
            href: '/admin/analytics'
          },
          {
            label: 'Pending Extensions',
            value: dashboardStats.pendingExtensions,
            icon: <AlertTriangle size={24} />,
            color: dashboardStats.pendingExtensions > 0 ? 'text-yellow-500' : 'text-green-500',
            href: '/admin/extensions'
          },
          {
            label: 'Published Content',
            value: dashboardStats.publishedContent,
            icon: <CheckCircle2 size={24} />,
            color: 'text-cyan-500',
            href: '/admin/content'
          },
        ]);

        setRecentActivity(activityLogs);
      } catch (err) {
        console.error('Failed to load dashboard data:', err);
        setError('Failed to load dashboard data. Please try again.');
      } finally {
        setLoading(false);
      }
    }

    loadDashboardData();
  }, []);

  const quickActions: QuickAction[] = [
    {
      label: 'Create Incident',
      description: 'Report a new system incident',
      href: '/admin/status',
      icon: <AlertTriangle size={20} />,
      color: 'from-red-500 to-orange-500'
    },
    {
      label: 'View Analytics',
      description: 'Check system performance',
      href: '/admin/analytics',
      icon: <TrendingUp size={20} />,
      color: 'from-blue-500 to-cyan-500'
    },
    {
      label: 'Manage Extensions',
      description: 'Review and approve extensions',
      href: '/admin/extensions',
      icon: <Package size={20} />,
      color: 'from-purple-500 to-pink-500'
    },
    {
      label: 'Visit Website',
      description: 'Go to public website',
      href: '/',
      icon: <Globe size={20} />,
      color: 'from-green-500 to-emerald-500'
    },
  ];

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  // Show loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 animate-spin text-accent" />
          <p className="text-text-secondary">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-4 text-center">
          <AlertTriangle className="w-12 h-12 text-red-500" />
          <p className="text-text-primary font-medium">Something went wrong</p>
          <p className="text-text-secondary">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-accent text-white rounded-lg hover:bg-accent/90 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Welcome Section */}
      <div className="bg-gradient-to-r from-accent/10 to-purple-500/10 rounded-2xl p-6 border border-accent/20">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-text-primary mb-2">
              {getGreeting()}, {session?.user.name}!
            </h1>
            <p className="text-text-secondary">
              Welcome to the Ochtopus Admin Panel. Here's what's happening today.
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm text-text-tertiary">
              {new Date().toLocaleDateString('en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </p>
            <p className="text-xs text-text-tertiary mt-1">
              Last login: {session?.user.lastLogin ? new Date(session.user.lastLogin).toLocaleString() : 'N/A'}
            </p>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat, index) => (
          <Link
            key={index}
            to={stat.href || '#'}
            className="bg-bg-card border border-border-default rounded-xl p-5 hover:border-accent/50 transition-colors group"
          >
            <div className="flex items-start justify-between mb-3">
              <div className={cn('p-2 rounded-lg bg-bg-elevated', stat.color)}>
                {stat.icon}
              </div>
              {stat.change && (
                <div className={cn(
                  'flex items-center gap-1 text-xs px-2 py-1 rounded-full',
                  stat.change.trend === 'up' ? 'bg-green-500/10 text-green-500' : 'bg-red-500/10 text-red-500'
                )}>
                  {stat.change.trend === 'up' ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                  {stat.change.value}%
                </div>
              )}
            </div>
            <p className="text-2xl font-bold text-text-primary mb-1">{stat.value}</p>
            <p className="text-sm text-text-secondary group-hover:text-accent transition-colors">{stat.label}</p>
          </Link>
        ))}
      </div>

      {/* Quick Actions & Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Quick Actions */}
        <div className="lg:col-span-1">
          <h2 className="text-lg font-semibold text-text-primary mb-4">Quick Actions</h2>
          <div className="space-y-3">
            {quickActions.map((action, index) => (
              <Link
                key={index}
                to={action.href}
                className="flex items-center gap-4 p-4 bg-bg-card border border-border-default rounded-xl hover:border-accent/50 transition-colors group"
              >
                <div className={cn('p-2 rounded-lg bg-gradient-to-br text-white', action.color)}>
                  {action.icon}
                </div>
                <div className="flex-1">
                  <p className="font-medium text-text-primary group-hover:text-accent transition-colors">
                    {action.label}
                  </p>
                  <p className="text-xs text-text-tertiary">{action.description}</p>
                </div>
                <ArrowRight size={16} className="text-text-tertiary group-hover:text-accent transition-colors" />
              </Link>
            ))}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="lg:col-span-2">
          <h2 className="text-lg font-semibold text-text-primary mb-4">Recent Activity</h2>
          <div className="bg-bg-card border border-border-default rounded-xl overflow-hidden">
            <div className="divide-y divide-border-default">
              {recentActivity.length === 0 ? (
                <div className="p-8 text-center">
                  <Activity className="w-8 h-8 text-text-tertiary mx-auto mb-2" />
                  <p className="text-text-secondary">No recent activity</p>
                </div>
              ) : (
                recentActivity.map((activity) => (
                  <div key={activity.id} className="flex items-center gap-4 p-4">
                    <div className={cn(
                      'w-2 h-2 rounded-full flex-shrink-0',
                      activity.type === 'success' && 'bg-green-500',
                      activity.type === 'warning' && 'bg-yellow-500',
                      activity.type === 'info' && 'bg-blue-500',
                      activity.type === 'error' && 'bg-red-500'
                    )} />
                    <p className="flex-1 text-sm text-text-primary">{activity.message}</p>
                    <div className="flex items-center gap-1 text-xs text-text-tertiary">
                      <Clock size={12} />
                      {formatRelativeTime(activity.timestamp)}
                    </div>
                  </div>
                ))
              )}
            </div>
            <div className="p-3 bg-bg-elevated border-t border-border-default">
              <button className="w-full text-center text-sm text-accent hover:underline">
                View All Activity
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* System Health Overview */}
      <div className="bg-bg-card border border-border-default rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-text-primary">System Health</h2>
          <Link to="/admin/status" className="text-sm text-accent hover:underline flex items-center gap-1">
            View Details <ArrowRight size={14} />
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {[
            { id: 'api', name: 'API', icon: <Server size={24} /> },
            { id: 'database', name: 'Database', icon: <Server size={24} /> },
            { id: 'auth', name: 'Authentication', icon: <Zap size={24} /> },
            { id: 'storage', name: 'Storage', icon: <Server size={24} /> },
            { id: 'cdn', name: 'CDN', icon: <Globe size={24} /> },
            { id: 'analytics', name: 'Analytics', icon: <Activity size={24} /> },
          ].map(service => (
            <div key={service.id} className="text-center">
              <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-2 bg-green-500/10">
                <CheckCircle2 className="text-green-500" size={24} />
              </div>
              <p className="text-xs text-text-primary font-medium truncate">{service.name}</p>
              <p className="text-xs text-green-500">Operational</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
